import React, { useEffect, useState } from "react";
import client, { fmtPct } from "../lib/api";
import Sparkline from "./Sparkline";

const fmtVal = (n, unit) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (unit === "%") return `${Number(n).toFixed(2)}%`;
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
};

const Section = ({ title, items }) => (
  <div className="glass p-6">
    <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">{title}</div>
    <div className="space-y-2">
      {items.map((x) => (
        <div
          key={x.symbol}
          data-testid={`global-${x.symbol}`}
          className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{x.name}</div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest">{x.unit}</div>
          </div>
          {x.history?.length > 1 && <Sparkline data={x.history} width={64} height={22} />}
          <div className="text-right whitespace-nowrap">
            <div className="font-mono-num text-sm">{fmtVal(x.price, x.unit)}</div>
            <div className={`text-[11px] font-mono-num ${x.change_percent >= 0 ? "up" : "down"}`}>
              {x.change_percent !== null && x.change_percent !== undefined ? fmtPct(x.change_percent) : "—"}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const GlobalMarkets = () => {
  const [data, setData] = useState({ equities: [], bonds: [], metals: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchGlobal = () => {
      client.get("/markets/global").then(({ data }) => {
        if (cancelled) return;
        setData(data || {});
        setLoading(false);
      }).catch((e) => {
        console.error("global markets failed", e);
        if (!cancelled) setLoading(false);
      });
    };
    fetchGlobal();
    const id = setInterval(fetchGlobal, 60_000); // refresh every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <section data-testid="global-markets-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <span className="chip">Global Markets</span>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-3">Equities · Bonds · Precious Metals</h2>
          <p className="text-neutral-400 text-sm mt-1">Live macro data — refreshed every 5 minutes.</p>
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="glass p-6 h-72 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Section title="Equity Indices" items={data.equities || []} />
          <Section title="Bonds / Treasury Yields" items={data.bonds || []} />
          <Section title="Precious Metals" items={data.metals || []} />
        </div>
      )}
    </section>
  );
};

export default GlobalMarkets;
